import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import styled from "styled-components";

// 페이지 애니메이션 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

function IdCardCameraPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const guideFrameRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState(null);
  const [usingFrontCamera, setUsingFrontCamera] = useState(false);
  const [showUploadOption, setShowUploadOption] = useState(false);
  const [isCameraSupported, setIsCameraSupported] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const fileInputRef = useRef(null);

  // 카메라 권한 상태 확인 함수
  const checkCameraPermission = async () => {
    try {
      // 권한 API가 지원되는 경우
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({
          name: "camera",
        });

        if (permissionStatus.state === "denied") {
          setPermissionDenied(true);
          setIsCameraSupported(false);
          setShowUploadOption(true);
          return false;
        }

        // 권한 상태 변경 감지
        permissionStatus.onchange = () => {
          if (permissionStatus.state === "granted") {
            setPermissionDenied(false);
            startCamera();
          } else if (permissionStatus.state === "denied") {
            setPermissionDenied(true);
            setIsCameraSupported(false);
            setShowUploadOption(true);
          }
        };
      }
    } catch (err) {
      // 권한 API가 지원되지 않는 경우 무시하고 계속 진행
    }
    return true;
  };

  // 카메라 지원 및 상태 테스트 함수
  const testCameraSupport = async () => {
    try {
      // HTTPS 체크 (localhost 제외)
      const isSecure =
        window.location.protocol === "https:" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (!isSecure) {
        setError(
          "카메라 접근을 위해서는 HTTPS 연결이 필요합니다. 안전한 연결로 접속해주세요."
        );
        setIsCameraSupported(false);
        setShowUploadOption(true);
        return false;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setIsCameraSupported(false);
        setShowUploadOption(true);
        return false;
      }

      // 권한 상태 먼저 확인
      const hasPermission = await checkCameraPermission();
      if (!hasPermission) {
        return false;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      if (!stream || !stream.active) {
        setIsCameraSupported(false);
        setShowUploadOption(true);
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack || videoTrack.readyState !== "live") {
        setIsCameraSupported(false);
        setShowUploadOption(true);
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }

      const testVideo = document.createElement("video");
      testVideo.srcObject = stream;

      try {
        await new Promise((resolve, reject) => {
          testVideo.onloadedmetadata = resolve;
          testVideo.onerror = reject;
          setTimeout(reject, 5000);
        });

        await testVideo.play();

        stream.getTracks().forEach((track) => track.stop());
        return true;
      } catch (err) {
        stream.getTracks().forEach((track) => track.stop());
        setIsCameraSupported(false);
        setShowUploadOption(true);
        return false;
      }
    } catch (err) {
      setIsCameraSupported(false);
      setShowUploadOption(true);

      // 권한 거부 에러인 경우
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setPermissionDenied(true);
        setError(
          "카메라 접근 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요."
        );
      }

      return false;
    }
  };

  // 컴포넌트 마운트 시 카메라 지원 테스트 후 시작
  useEffect(() => {
    const initializeCamera = async () => {
      const isSupported = await testCameraSupport();

      if (isSupported) {
        startCamera();
      } else {
        setShowUploadOption(true);
      }
    };

    initializeCamera();

    return () => {
      stopCamera();
    };
  }, []);

  // 카메라 시작 함수
  const startCamera = async (forceFront = false) => {
    try {
      // 이전 카메라 중지
      stopCamera();

      const facingMode = forceFront ? "user" : "environment";
      setUsingFrontCamera(forceFront);

      // 카메라 제약 조건
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // 스트림 참조 저장
      streamRef.current = stream;

      // 비디오 요소에 스트림 연결
      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        videoRef.current.onloadedmetadata = () => {
          videoRef.current
            .play()
            .then(() => {
              setCameraActive(true);
              setError(null);
            })
            .catch((err) => {
              setError("카메라 재생에 실패했습니다. 다시 시도해주세요.");
            });
        };
      }
    } catch (err) {
      // 카메라 접근 실패 시 파일 업로드 옵션 표시
      setIsCameraSupported(false);
      setShowUploadOption(true);
      setCameraActive(false);

      let errorMessage = "카메라 접근에 실패했습니다. ";

      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setPermissionDenied(true);
        errorMessage =
          "카메라 접근 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.";
      } else if (err.name === "NotFoundError") {
        errorMessage += "카메라를 찾을 수 없습니다.";
      } else if (err.name === "NotReadableError") {
        errorMessage += "카메라가 다른 앱에서 사용 중일 수 있습니다.";
      } else {
        errorMessage += err.message || "알 수 없는 오류가 발생했습니다.";
      }

      setError(errorMessage);
    }
  };

  // 카메라 중지 함수
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  // 카메라 전환 함수
  const switchCamera = () => {
    startCamera(!usingFrontCamera);
  };

  // 이미지 파일 업로드 처리 함수
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      // 파일 선택이 취소된 경우 다시 카메라 실행
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("이미지 파일만 업로드 가능합니다.");
      // 잘못된 파일 형식인 경우 다시 카메라 실행
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      navigate("/id-card-crop", {
        state: {
          originalImage: e.target.result,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  // 사진 촬영 함수 - 가이드 영역만 잘라내기
  const capturePhoto = () => {
    if (!isCameraSupported || showUploadOption) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    if (!videoRef.current) {
      console.error("videoRef가 없습니다.");
      return;
    }

    if (!cameraActive) {
      console.error("카메라가 활성화되지 않았습니다.");
      setError("카메라가 활성화되지 않았습니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    try {
      // 가이드 프레임 요소 가져오기
      const guideFrame = guideFrameRef.current;
      if (!guideFrame) {
        console.error("가이드 프레임을 찾을 수 없습니다.");
        return;
      }

      // 비디오 및 화면 정보 수집
      const videoRect = videoRef.current.getBoundingClientRect();
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;
      const displayWidth = videoRect.width;
      const displayHeight = videoRect.height;

      // 가이드 프레임 정보 수집
      const frameRect = guideFrame.getBoundingClientRect();

      // 임시 캔버스에 전체 비디오 프레임 그리기
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = videoWidth;
      tempCanvas.height = videoHeight;
      const tempContext = tempCanvas.getContext("2d");

      // 전면 카메라 처리 (필요한 경우 좌우 반전)
      if (usingFrontCamera) {
        tempContext.translate(tempCanvas.width, 0);
        tempContext.scale(-1, 1);
      }

      tempContext.drawImage(videoRef.current, 0, 0, videoWidth, videoHeight);

      // 비디오 내에서 가이드 프레임의 정확한 위치 계산
      // 화면상 좌표를 비디오 내부 좌표로 변환
      const scaleX = videoWidth / displayWidth;
      const scaleY = videoHeight / displayHeight;

      // 비디오 요소 내에서 가이드 프레임의 상대적 위치
      const relativeLeft = frameRect.left - videoRect.left;
      const relativeTop = frameRect.top - videoRect.top;

      // 비디오 내부 좌표로 변환
      let cropX = Math.floor(relativeLeft * scaleX);
      let cropY = Math.floor(relativeTop * scaleY);
      let cropWidth = Math.floor(frameRect.width * scaleX);
      let cropHeight = Math.floor(frameRect.height * scaleY);

      // 전면 카메라 경우 좌표 조정 (좌우 반전 고려)
      if (usingFrontCamera) {
        cropX = videoWidth - cropX - cropWidth;
      }

      // 크롭 영역이 비디오를 벗어나지 않도록 조정
      cropX = Math.max(0, Math.min(cropX, videoWidth - 10));
      cropY = Math.max(0, Math.min(cropY, videoHeight - 10));
      cropWidth = Math.min(cropWidth, videoWidth - cropX);
      cropHeight = Math.min(cropHeight, videoHeight - cropY);

      // 최종 이미지를 위한 크롭 캔버스 생성
      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = 325; // 340에서 325로 변경
      cropCanvas.height = 208;
      const cropContext = cropCanvas.getContext("2d");

      // 정확한 비율 계산을 위한 값
      const targetRatio = 325 / 208; // 원하는 비율 (가로/세로)
      const sourceRatio = cropWidth / cropHeight; // 원본 크롭 영역 비율

      // 원본과 목표 비율 간의 차이 조정
      let adjustedCropX = cropX;
      let adjustedCropY = cropY;
      let adjustedCropWidth = cropWidth;
      let adjustedCropHeight = cropHeight;

      // 이미지를 약간 아래로 이동 (Y 좌표 조정)
      const offsetY = Math.floor(cropHeight * 0.08); // 높이의 약 8% 정도 아래로 이동
      adjustedCropY += offsetY;

      // 조정된 영역이 비디오 밖으로 벗어나지 않도록 확인
      if (adjustedCropY + adjustedCropHeight > videoHeight) {
        adjustedCropY = videoHeight - adjustedCropHeight;
      }

      // 비율이 다를 경우 중앙 정렬로 조정
      if (Math.abs(sourceRatio - targetRatio) > 0.05) {
        // 약간의 오차 허용
        if (sourceRatio > targetRatio) {
          // 너무 넓은 경우 폭 조정 (가운데 정렬)
          const newWidth = cropHeight * targetRatio;
          adjustedCropX = cropX + (cropWidth - newWidth) / 2;
          adjustedCropWidth = newWidth;
        } else {
          // 너무 좁은 경우 높이 조정 (가운데 정렬)
          const newHeight = cropWidth / targetRatio;
          adjustedCropY = Math.min(
            cropY + offsetY + (cropHeight - newHeight) / 2,
            videoHeight - newHeight
          );
          adjustedCropHeight = newHeight;
        }
      }

      // 크롭된 영역만 추출하여 그리기
      cropContext.drawImage(
        tempCanvas,
        adjustedCropX,
        adjustedCropY,
        adjustedCropWidth,
        adjustedCropHeight, // 소스 영역 (조정된 좌표)
        0,
        0,
        325, // 340에서 325로 변경
        208
      );

      // 이미지 데이터 URL 생성
      const imageDataURL = cropCanvas.toDataURL("image/jpeg", 0.95);

      // 이미지 저장 및 페이지 이동
      localStorage.setItem("temp_captured_image", imageDataURL);

      // 디버깅용 치수 정보 저장
      const imageDimensions = {
        frameRect: {
          left: frameRect.left - videoRect.left,
          top: frameRect.top - videoRect.top,
          width: frameRect.width,
          height: frameRect.height,
        },
        cropCoords: {
          x: cropX,
          y: cropY,
          width: cropWidth,
          height: cropHeight,
        },
        videoSize: {
          width: videoWidth,
          height: videoHeight,
        },
        displaySize: {
          width: displayWidth,
          height: displayHeight,
        },
        scale: {
          x: scaleX,
          y: scaleY,
        },
        usingFrontCamera,
      };

      localStorage.setItem(
        "temp_captured_dimensions",
        JSON.stringify(imageDimensions)
      );

      // 제출 페이지로 이동

      // 약간의 지연 후 이동하여 localStorage 저장이 완료되도록 함
      setTimeout(() => {
        navigate("/id-card-submit", {
          replace: true,
          state: { fromCamera: true },
        });
      }, 100);
    } catch (err) {
      console.error("이미지 캡처 오류:", err);
      setError(`이미지 캡처 오류: ${err.message}`);
    }
  };

  // 뒤로가기
  const handleBack = () => {
    navigate("/id-card-submit");
  };

  return (
    <CameraPage
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <CameraHeader>
        <CameraBackButton onClick={handleBack}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CameraBackButton>
        <h1>신분증 촬영</h1>
        <CameraCloseButton onClick={handleBack}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6L18 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CameraCloseButton>
      </CameraHeader>

      <CameraContainerFullscreen>
        <CameraViewFullscreen
          ref={videoRef}
          autoPlay
          playsInline
          style={{ transform: usingFrontCamera ? "scaleX(-1)" : "none" }}
        />

        {/* 카메라 접근에 문제가 없을 때만 가이드 오버레이 표시 */}
        {(!showUploadOption || cameraActive) && (
          <IdCardGuideOverlay>
            <IdCardGuideFrame ref={guideFrameRef}></IdCardGuideFrame>
            <IdCardGuideTextContainer>
              <IdCardGuideText>
                표시된 영역에 신분증을 맞춰 주세요.
              </IdCardGuideText>
              <IdCardGuideSubtext>
                - 신분증 전체를 촬영해 주세요.
                <br />
                - 어두운 배경에서 촬영해 주세요.
                <br />- 빛이 반사되지 않도록 방향을 조정해 주세요.
              </IdCardGuideSubtext>
            </IdCardGuideTextContainer>
          </IdCardGuideOverlay>
        )}

        {error && showUploadOption && <ErrorMessage>{error}</ErrorMessage>}
      </CameraContainerFullscreen>
      {(!showUploadOption || cameraActive) && (
        <CameraControls>
          <CameraControlButton onClick={switchCamera}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 11.5V13H9v2.5L5.5 12 9 8.5V11h6V8.5l3.5 3.5-3.5 3.5z"
                fill="white"
              />
            </svg>
          </CameraControlButton>
          <CaptureButton onClick={capturePhoto}></CaptureButton>
          <CameraControlPlaceholder></CameraControlPlaceholder>
        </CameraControls>
      )}

      {/* 캔버스는 숨겨둠 */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* 파일 업로드 입력 (숨겨짐) */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        style={{ display: "none" }}
      />

      {/* 카메라 지원되지 않을 때 안내 메시지 */}
      {showUploadOption && !cameraActive && (
        <CameraAlternative>
          <UploadOption>
            <h3>카메라 접근에 문제가 있습니다</h3>
            {permissionDenied ? (
              <>
                <p
                  style={{
                    marginBottom: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  카메라 접근 권한이 필요합니다.브라우저 설정에 들어가셔서
                  권한을 허용해주세요.
                </p>
              </>
            ) : (
              <p>
                신분증을 촬영하신 후, 신분증 규격에 맞게 이미지를 조정하여
                업로드해 주시기 바랍니다.
              </p>
            )}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <UploadButton
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
              >
                갤러리에서 선택
              </UploadButton>
              {!permissionDenied && (
                <RetryButton
                  onClick={() => {
                    setShowUploadOption(false);
                    setError(null);
                    setPermissionDenied(false);
                    startCamera();
                  }}
                >
                  카메라 다시 시도
                </RetryButton>
              )}
            </div>
          </UploadOption>
        </CameraAlternative>
      )}
    </CameraPage>
  );
}

// 스타일 컴포넌트
const CameraPage = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CameraHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 101;

  h1 {
    color: white;
    font-size: 18px;
    font-weight: 500;
    margin: 0;
  }
`;

const CameraBackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CameraCloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CameraContainerFullscreen = styled.div`
  position: relative;
  width: 100%;
  height: 80vh;
  overflow: hidden;
  background-color: #111;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CameraViewFullscreen = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const IdCardGuideOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 100;
  pointer-events: none;
`;

const IdCardGuideFrame = styled.div`
  width: 325px;
  height: 208px;
  min-height: auto;
  max-height: none;
  max-width: 90%;
  border: 2px solid #ff6b00;
  border-radius: 8px;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.7);
  position: relative;
  left: 0;
  right: 0;
  margin: 0 auto;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 25px;
    height: 25px;
    border-color: #ff6b00;
    border-style: solid;
  }

  &::before {
    top: -2px;
    left: -2px;
    border-width: 5px 0 0 5px;
    border-radius: 5px 0 0 0;
  }

  &::after {
    bottom: -2px;
    right: -2px;
    border-width: 0 5px 5px 0;
    border-radius: 0 0 5px 0;
  }
`;

const IdCardGuideTextContainer = styled.div`
  z-index: 102;
  margin-top: 40px;
  text-align: center;
  padding: 12px 20px;
  border-radius: 12px;
  max-width: 90%;
`;

const IdCardGuideText = styled.p`
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
  opacity: 1;
`;

const IdCardGuideSubtext = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  text-align: center;
  font-weight: 400;
  line-height: 1.5;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  opacity: 1;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  max-width: 90%;
  text-align: center;
  z-index: 1000;
`;

const CameraControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 40px 40px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  z-index: 101;
`;

const CameraControlButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background-color: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const CameraControlPlaceholder = styled.div`
  width: 44px;
  height: 44px;
`;

const CaptureButton = styled.button`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background-color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 0 0 5px rgba(255, 255, 255, 0.3);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 4px;
    left: 4px;
    right: 4px;
    bottom: 4px;
    border-radius: 50%;
    border: 2px solid rgba(0, 0, 0, 0.1);
  }
`;

const CameraAlternative = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f9f9f9;
  z-index: 10;
  padding: 20px;
`;

const UploadOption = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 320px;
  text-align: center;

  h3 {
    font-size: 18px;
    margin-bottom: 12px;
    color: #333;
  }

  p {
    font-size: 14px;
    color: #666;
    margin-bottom: 24px;
  }
`;

const UploadButton = styled.button`
  background-color: #7a4fff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;

  &:hover,
  &:focus {
    background-color: #0066e0;
  }
`;

const RetryButton = styled.button`
  width: 100%;
  padding: 16px;
  background-color: transparent;
  color: #7b42f6;
  border: 2px solid #7b42f6;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(123, 66, 246, 0.1);
  }
`;

export default IdCardCameraPage;
